import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Heart, Baby, FirstAidKit, Bell, TestTube } from "@phosphor-icons/react";
import { formatDate } from "@/lib/constants";
import LoadError from "@/components/LoadError";

function Section({ icon: Icon, title, tone, description, empty, children, testid }) {
  const toneCls = {
    green: "text-[#2A5A3B] bg-[#E9F5EC] border-[#2A5A3B]/20",
    amber: "text-[#8B5A00] bg-[#FFF6E5] border-[#D4A017]/30",
    red: "text-[#C05746] bg-[#FDEDED] border-[#C05746]/20",
  }[tone];
  return (
    <section data-testid={testid} className="bg-white border border-[#E2E0D8] rounded-sm">
      <header className="p-5 border-b border-[#E2E0D8] flex items-center gap-3">
        <div className={`w-10 h-10 rounded-sm border flex items-center justify-center ${toneCls}`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-lg tracking-tight">{title}</h3>
          <p className="text-xs text-[#4E544B]">{description}</p>
        </div>
      </header>
      <div className="p-1">
        {(!children || (Array.isArray(children) && children.length === 0)) ? (
          <div className="p-6 text-sm text-[#4E544B] text-center">{empty}</div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

function AlertRow({ to, testid, primary, secondary, right, rightTone }) {
  const toneCls = {
    green: "text-[#2A5A3B]",
    amber: "text-[#8B5A00]",
    red: "text-[#C05746]",
  }[rightTone];
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center justify-between gap-3 p-4 border-b border-[#E2E0D8] last:border-b-0 hover:bg-[#F6F5F2] transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="font-bold truncate">{primary}</div>
        <div className="text-xs text-[#4E544B] truncate">{secondary}</div>
      </div>
      <div className={`text-sm font-bold whitespace-nowrap ${toneCls}`}>{right}</div>
    </Link>
  );
}

function relTone(days, kind) {
  if (kind === "cio") {
    if (days < 0) return "red";
    if (days === 0) return "green";
    return "amber";
  }
  if (kind === "parto") {
    if (days <= 3) return "red";
    if (days <= 14) return "amber";
    return "green";
  }
  if (kind === "carencia") {
    if (days <= 0) return "green";
    if (days <= 3) return "red";
    return "amber";
  }
  return "green";
}

function relLabel(days, kind) {
  if (days === 0) return kind === "carencia" ? "termina hoje" : "hoje";
  if (days > 0) return `em ${days}d`;
  return `${Math.abs(days)}d atrás`;
}

export default function Alertas() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null);
    api
      .get("/alertas")
      .then((r) => setData(r.data))
      .catch((e) =>
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message),
      );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <LoadError error={error} onRetry={load} />;
  if (!data) return <div className="text-[#4E544B]">Carregando alertas…</div>;

  return (
    <div data-testid="alertas-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <Bell size={28} weight="duotone" color="#2A5A3B" />
        <div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Alertas</h1>
          <p className="text-sm text-[#4E544B] mt-1">
            {data.total} alertas ativos no rebanho
          </p>
        </div>
      </div>

      {data.total === 0 && (
        <div className="bg-white border border-[#E2E0D8] rounded-sm p-8 text-center text-[#4E544B]">
          Nenhum alerta ativo neste momento. Volte após novos registros.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section
          testid="section-cios"
          icon={Heart}
          tone="red"
          title="Cios previstos"
          description="21 dias após o último cio (janela -3 a +7 dias)"
          empty="Nenhum cio previsto."
        >
          {data.cios_previstos.map((a) => (
            <AlertRow
              key={"cio-" + a.animal_id}
              testid={`cio-${a.animal_numero}`}
              to={`/animais/${a.animal_id}`}
              primary={`${a.animal_numero}${a.animal_nome ? " · " + a.animal_nome : ""}`}
              secondary={`Último cio: ${formatDate(a.data_ultimo_cio)} · Previsto: ${formatDate(a.data_prevista)}`}
              right={relLabel(a.dias, "cio")}
              rightTone={relTone(a.dias, "cio")}
            />
          ))}
        </Section>

        <Section
          testid="section-pre-parto"
          icon={Baby}
          tone="amber"
          title="Pré-parto"
          description="~280 dias após a inseminação (janela -30 a +14)"
          empty="Nenhum pré-parto no horizonte."
        >
          {data.pre_parto.map((a) => (
            <AlertRow
              key={"pp-" + a.animal_id}
              testid={`preparto-${a.animal_numero}`}
              to={`/animais/${a.animal_id}`}
              primary={`${a.animal_numero}${a.animal_nome ? " · " + a.animal_nome : ""}`}
              secondary={`IA: ${formatDate(a.data_inseminacao)} · Touro: ${a.touro || "-"} · Previsto: ${formatDate(a.data_prevista_parto)}`}
              right={relLabel(a.dias, "parto")}
              rightTone={relTone(a.dias, "parto")}
            />
          ))}
        </Section>

        <Section
          testid="section-carencia"
          icon={FirstAidKit}
          tone="green"
          title="Carência de medicamento"
          description="Dias restantes até liberar leite/carne"
          empty="Nenhuma carência ativa."
        >
          {data.carencia_medicamento.map((a) => (
            <AlertRow
              key={"car-" + a.animal_id + "-" + a.data_tratamento}
              testid={`carencia-${a.animal_numero}`}
              to={`/animais/${a.animal_id}`}
              primary={`${a.animal_numero}${a.animal_nome ? " · " + a.animal_nome : ""}`}
              secondary={`${a.tratamento}${a.medicamento ? " (" + a.medicamento + ")" : ""} · Fim: ${formatDate(a.data_fim_carencia)}`}
              right={relLabel(a.dias_restantes, "carencia")}
              rightTone={relTone(a.dias_restantes, "carencia")}
            />
          ))}
        </Section>

        <Section
          testid="section-mastite"
          icon={TestTube}
          tone="red"
          title="Mastite / CCS elevado"
          description="CCS ≥ 200 mil/mL ou mastite clínica nos últimos 14 dias"
          empty="Nenhum caso de mastite ativo."
        >
          {(data.mastite || []).map((a) => {
            const isCcs = a.tipo === "ccs_alta";
            const rightLabel = isCcs
              ? `${Number(a.ccs_mil_ml).toFixed(0)} mil/mL`
              : "clínica";
            const tone =
              a.gravidade === "severa" || a.gravidade === "clinica"
                ? "red"
                : "amber";
            const secondary = isCcs
              ? `CCS ${a.gravidade === "severa" ? "severa" : "suspeita"} · Quarto: ${
                  { GERAL: "Geral", AD: "Ant. dir.", AE: "Ant. esq.", PD: "Post. dir.", PE: "Post. esq." }[a.quarto] || a.quarto
                } · ${formatDate(a.data)}`
              : `Mastite clínica tratada${a.medicamento ? " com " + a.medicamento : ""} · ${formatDate(a.data)}`;
            return (
              <AlertRow
                key={`mas-${a.tipo}-${a.animal_id}-${a.data}`}
                testid={`mastite-${a.animal_numero}`}
                to={`/animais/${a.animal_id}`}
                primary={`${a.animal_numero}${a.animal_nome ? " · " + a.animal_nome : ""}`}
                secondary={secondary}
                right={rightLabel}
                rightTone={tone}
              />
            );
          })}
        </Section>
      </div>
    </div>
  );
}
